import { Link } from "react-router-dom";

/**
 * About page — a personal-homepage-style bio of Casey Scott McKay, consistent
 * with the archive's persona: scuba diving, surfing, and travel, plus an
 * archivist's fondness for accession numbers.
 */
export function AboutPage() {
  return (
    <div className="page">
      <div className="container">
        <header className="page__head">
          <p className="eyebrow">About</p>
          <h1 className="page__title">About Casey Scott McKay</h1>
          <p className="page__intro">
            Diver, surfer, and serial traveller — and a compulsive cataloguer of
            all three. This site is where I keep the footage, the photos, and the
            notes around them.
          </p>
        </header>

        <div className="prose" style={{ maxWidth: "70ch" }}>
          <p>
            Hi — I'm Casey. I spend as much time as I can underwater, in the
            surf, or somewhere I've never been, and I bring back photos, video,
            and a lot of notes. I started this archive because my dive logs,
            clips, and trip notes were scattered across phones, drives, and the
            backs of boarding passes, and I wanted one place that worked like a
            small library: every item gets a number, a date, and a place it
            belongs.
          </p>

          <h2>What I shoot</h2>
          <p>
            Reefs and the things that live on them — grey reef sharks stacked up
            in the current at Blue Corner, green turtles cruising a Red Sea wall,
            mantas barrel-rolling through the lights on a night dive. Above the
            surface it's mostly waves: dawn patrols on the Bukit, long lefts that
            make me look better than I am. Most of it is shot on a beat-up
            action camera in a housing, with a wide lens and a lot of patience.
          </p>

          <h2>The field notes</h2>
          <p>
            A lot of the work comes out of trips. A week on a Red Sea liveaboard
            turned into a whole series; so did a Palau shark-diving run and a
            string of mornings at Uluwatu. The <Link to="/blog">Journal</Link> is
            where those stories live — trip reports, dive logs, and the
            occasional tangent about gear, currents, or how to time a tide.
          </p>

          <h2>Why the accession numbers?</h2>
          <p>
            Because they work. Borrowed straight from how museums track their
            collections, every photo, video, journal entry, and reference here
            carries a short code — <span className="mono">PH-0007</span>,{" "}
            <span className="mono">VD-0002</span>, and so on. It looks fussy. It
            is fussy. It also means I can find any dive or any clip in seconds,
            and so can you, from the <Link to="/search">search page</Link>.
          </p>

          <h2>This website</h2>
          <p>
            Built plainly and on purpose. No autoplaying anything, no infinite
            scroll, no algorithm deciding what you should see next — just a
            catalogue you can browse, search, and talk back to. Every item has a
            comment thread, so if you've dived the same site, surfed the same
            reef, or know a better spot, tell me.
          </p>

          <hr />

          <p className="mono" style={{ fontSize: "0.85rem" }}>
            Want to get in touch? Head to the{" "}
            <Link to="/contact">contact page</Link> and sign the guestbook — er,
            send a message.
          </p>
        </div>
      </div>
    </div>
  );
}
